import { resolveStoredFileUrl } from "utils/storedFileUrl";
import { gql, useLazyQuery } from "@apollo/client";
import { Card, Collapse, Fade, Grid, Icon, Tooltip } from "@mui/material";
import logo from "assets/images/comedor.png";
import logoAlt from "assets/images/comedor_alt.png";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { useEffect, useRef, useState } from "react";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto, makeNiceText } from "utils/formatters";

function FixedShapesBackground() {
  const canvasRef = useRef(null);
  const shapesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);
    let animationFrameId;

    const defineShapes = (currentW, currentH) => [
      {
        type: "circle",
        color: "#67C1B8",
        x: currentW * 0.15,
        y: currentH * 0.2,
        size: Math.min(currentW, currentH) * 0.15,
        angle: Math.PI / 6,
        animOffset: Math.random() * 10,
        animSpeed: 0.0005,
        animMagnitude: Math.min(currentW, currentH) * 0.015,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 0.8,
        y: currentH * 0.3,
        size: Math.min(currentW, currentH) * 0.22,
        angle: -Math.PI / 4,
        animOffset: Math.random() * 10,
        animSpeed: 0.0006,
        animMagnitude: Math.min(currentW, currentH) * 0.01,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 0,
        y: currentH * 0.12,
        size: Math.min(currentW, currentH) * 0.22,
        angle: -Math.PI / 4,
        animOffset: Math.random() * 10,
        animSpeed: 0.0006,
        animMagnitude: Math.min(currentW, currentH) * 0.01,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 0.23,
        y: currentH * 1,
        size: Math.min(currentW, currentH) * 0.22,
        angle: -Math.PI / 4,
        animOffset: Math.random() * 10,
        animSpeed: 0.0006,
        animMagnitude: Math.min(currentW, currentH) * 0.01,
      },
      {
        type: "x",
        color: "#ffffff",
        x: currentW * 0.75,
        y: currentH * 0.8,
        size: Math.min(currentW, currentH) * 0.13,
        angle: 0,
        animOffset: Math.random() * 10,
        animSpeed: 0.001,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "x",
        color: "#ffffff",
        x: currentW * 0.13,
        y: currentH * 0.9,
        size: Math.min(currentW, currentH) * 0.05,
        angle: 130 * (Math.PI / 180),
        animOffset: Math.random() * 10,
        animSpeed: 0.001,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "x",
        color: "#ffffff",
        x: currentW * 0.19,
        y: currentH * 0.23,
        size: Math.min(currentW, currentH) * 0.04,
        angle: 45 * (Math.PI / 180),
        animOffset: Math.random() * 10,
        animSpeed: 0.001,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "circle",
        color: "#67C1B8",
        x: currentW * 1,
        y: currentH * 0.1,
        size: Math.min(currentW, currentH) * 0.16,
        angle: 0,
        animOffset: Math.random() * 10,
        animSpeed: 0.0007,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "circle",
        color: "#67C1B8",
        x: currentW * 0,
        y: currentH * 1,
        size: Math.min(currentW, currentH) * 0.35,
        angle: 0,
        animOffset: Math.random() * 20,
        animSpeed: 0.001,
        animMagnitude: Math.min(currentW, currentH) * 0.006,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 0.5,
        y: currentH * 0.1,
        size: Math.min(currentW, currentH) * 0.09,
        angle: Math.PI / 3,
        animOffset: Math.random() * 10,
        animSpeed: 0.0005,
        animMagnitude: Math.min(currentW, currentH) * 0.005,
      },
      {
        type: "donut",
        color: "#F59B24",
        x: currentW * 1,
        y: currentH * 1,
        size: Math.min(currentW, currentH) * 0.5,
        angle: Math.PI / 3,
        animOffset: Math.random() * 10,
        animSpeed: 0.0015,
        animMagnitude: Math.min(currentW, currentH) * 0.005,
      },
    ];

    shapesRef.current = defineShapes(w, h);

    const animate = (time) => {
      ctx.clearRect(0, 0, w, h);

      shapesRef.current.forEach((s) => {
        const currentY = s.y + Math.sin(time * s.animSpeed + s.animOffset) * s.animMagnitude;

        // Animación de shake para 'x'
        const angleShake =
          s.type === "x" ? Math.sin(time * s.animSpeed * 2 + s.animOffset) * (Math.PI / 32) : 0;

        ctx.save();
        ctx.translate(s.x, currentY);
        ctx.rotate(s.angle + angleShake);
        ctx.strokeStyle = s.color;
        ctx.fillStyle = s.color;
        ctx.lineWidth = Math.max(3, s.size / 25);
        ctx.globalAlpha = 1;

        switch (s.type) {
          case "circle":
            ctx.beginPath();
            ctx.arc(0, 0, s.size / 2, 0, 2 * Math.PI);
            ctx.fill();
            break;
          case "donut":
            const outerRadius = s.size / 2;
            const innerRadius = s.size / 4;
            ctx.beginPath();
            ctx.arc(0, 0, outerRadius, 0, 2 * Math.PI);
            ctx.fill();
            if (innerRadius > 0) {
              ctx.globalCompositeOperation = "destination-out";
              ctx.beginPath();
              ctx.arc(0, 0, innerRadius, 0, Math.PI * 2);
              ctx.fill();
              ctx.globalCompositeOperation = "source-over";
            }
            break;
          case "x":
            const maxLength = s.size / Math.SQRT2;
            const currentLength = maxLength / 2;
            ctx.beginPath();
            ctx.moveTo(-currentLength, -currentLength);
            ctx.lineTo(currentLength, currentLength);
            ctx.moveTo(currentLength, -currentLength);
            ctx.lineTo(-currentLength, currentLength);
            ctx.stroke();
            break;
        }

        ctx.restore();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    const handleResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      shapesRef.current = defineShapes(w, h);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}

const Carta = () => {
  const handleContextMenu = (e) => e.preventDefault();
  const [categorias, setCategorias] = useState([]);
  const [expanded, setExpanded] = useState([]);

  const [getProductos, { loading }] = useLazyQuery(
    gql`
      query getProductos($order: Order, $filter: JSON) {
        productosPublic(order: $order, filter: $filter) {
          id
          nombre
          disponibilidad
          precioCarta
          descripcion
          combo
          tipoProducto {
            id
            tipo
          }
          categoriaProducto {
            id
            categoria
          }
          productosCombo {
            id
            nombre
          }
          files {
            file
          }
        }
      }
    `
  );

  useEffect(() => {
    getProductos({
      variables: {
        filter: {
          lugarVenta: { _substring: 'Carta' }
        },
        order: { field: "nombre", order: "ASC" },
      },
    }).then((res) => {
      const productos = res?.data?.productosPublic;
      let newCategorias = [];
      (productos || []).forEach((producto) => {
        if (!producto.categoriaProducto) return;
        const categoria = producto.categoriaProducto.categoria;
        const index = newCategorias.findIndex((cat) => cat.categoria === categoria);
        if (index === -1) {
          newCategorias.push({
            categoria,
            productos: [producto],
          });
        } else {
          newCategorias[index].productos.push(producto);
        }
      });
      setCategorias(newCategorias);
    });
  }, []);

  return (
    <SoftBox
      onContextMenu={handleContextMenu}
      sx={{
        userSelect: "none",
        position: "relative",
        overflow: "hidden",
        overflowY: "auto",
        minHeight: "100vh",
        backgroundColor: getUproColor("uproPink"),
      }}
    >
      <FixedShapesBackground />
      <Fade in={loading} unmountOnExit>
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          sx={{
            backgroundColor: getUproColor("uproPink"),
          }}
        >
          <SoftBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
          >
            <SoftBox component="img" src={logoAlt} alt="UPrO Logo" width="15rem" />
            <SoftTypography variant="h4" align="center" color="white">
              Estamos buscando la carta...
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </Fade>
      <Fade in={!loading} unmountOnExit>
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          sx={{ position: "relative", zIndex: 1, p: { xs: 2, md: 4 } }}
        >
          <SoftBox sx={{ width: "90%", maxWidth: "1200px" }}>
            <SoftBox display="flex" justifyContent="center" alignItems="center">
              <SoftBox component="img" src={logoAlt} alt="UPrO Logo" width="15rem" p={3} />
            </SoftBox>

            <SoftBox display="flex" flexDirection="column" gap={2}>
              {categorias.map((categoria) => (
                <Card key={categoria.categoria} sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}>
                  <SoftBox display="flex" flexDirection="column" gap={2} p={2}>
                    <SoftBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      onClick={() => {
                        setExpanded((prev) =>
                          prev.includes(categoria.categoria)
                            ? prev.filter((cat) => cat !== categoria.categoria)
                            : [...prev, categoria.categoria]
                        );
                      }}
                      sx={{
                        cursor: "pointer",
                        zIndex: 1,
                      }}
                    >
                      <SoftBox display="flex" flexDirection="column">
                        <SoftTypography variant="h6">{categoria.categoria}</SoftTypography>
                        <SoftTypography variant="caption">
                          {categoria.productos.length} productos
                        </SoftTypography>
                      </SoftBox>
                      <Tooltip
                        title={expanded.includes(categoria.categoria) ? "Ocultar" : "Mostrar"}
                        placement="top"
                      >
                        <SoftButton color="primary" circular iconOnly>
                          <Icon
                            sx={{
                              rotate: expanded.includes(categoria.categoria) ? "180deg" : "0deg",
                              transition: "all 0.5s",
                            }}
                          >
                            expand_more
                          </Icon>
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                    <Collapse
                      in={expanded.includes(categoria.categoria)}
                      unmountOnExit
                      mountOnEnter
                    >
                      <Grid container spacing={3} pt={1}>
                        {categoria.productos.map((producto) => (
                          <Grid item xs={12} sm={6} md={4} key={producto.id}>
                            <Card
                              sx={{
                                borderRadius: 3,
                                overflow: "hidden",
                                border: "1px solid rgba(0, 0, 0, 0.12)",
                                transition: "transform 0.3s ease-in-out",
                                "&:hover": {
                                  transform: "scale(1.02)",
                                },
                              }}
                            >
                              {producto.files && producto.files.length > 0 ? (
                                <SoftBox
                                  sx={{
                                    position: "relative",
                                    width: "100%",
                                    paddingBottom: "100%",
                                    overflow: "hidden",
                                    backgroundColor: "#f5f5f5",
                                  }}
                                >
                                  <SoftBox
                                    component="img"
                                    src={resolveStoredFileUrl(producto.files[0].file)}
                                    alt={producto.nombre}
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      objectPosition: "center",
                                      transition: "transform 0.3s ease-in-out",
                                      cursor: "pointer",
                                      "&:hover": {
                                        transform: "scale(1.05)",
                                      },
                                    }}
                                  />
                                </SoftBox>
                              ) : (
                                <SoftBox
                                  sx={{
                                    position: "relative",
                                    width: "100%",
                                    paddingBottom: "100%",
                                    overflow: "hidden",
                                    backgroundColor: "#f5f5f5",
                                  }}
                                >
                                  <SoftBox
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    <SoftBox
                                      component="img"
                                      src={logo}
                                      alt={producto.nombre}
                                      sx={{
                                        width: "50%",
                                        height: "50%",
                                        objectFit: "contain",
                                        transition: "transform 0.3s ease-in-out",
                                        cursor: "pointer",
                                        "&:hover": {
                                          transform: "scale(1.05)",
                                        },
                                      }}
                                    />
                                  </SoftBox>
                                </SoftBox>
                              )}
                              <SoftBox
                                display="flex"
                                gap={2}
                                justifyContent="space-between"
                                alignItems="center"
                                p={2}
                              >
                                <SoftBox display="flex" flexDirection="column">
                                  <SoftTypography variant="h6">
                                    {makeNiceText(producto.nombre)}
                                  </SoftTypography>
                                  {producto?.tipoProducto?.tipo && (
                                    <SoftTypography variant="h7" fontWeight="regular">
                                      {producto.tipoProducto.tipo}
                                    </SoftTypography>
                                  )}
                                </SoftBox>
                                <SoftBox>
                                  <SoftBadge
                                    color="primary"
                                    badgeContent={`$${formatMoneyPunto(producto.precioCarta)}`}
                                    container
                                  />
                                </SoftBox>
                              </SoftBox>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Collapse>
                  </SoftBox>
                </Card>
              ))}
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </Fade>
    </SoftBox>
  );
};

export default Carta;
